import { PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, OnInit } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { catchError, filter, from, last, map, mergeMap, Observable, of, switchMap, tap } from 'rxjs';
import { FileSizePipe } from '../file-size.pipe';
import { FileUploadService } from '../file-upload.service';
import { SelectedFile } from '../selected-file.model';
import { escape } from '../string-helper';
import { Study } from '../study.service';
import { Upload, UploadFileInfo, UploadService } from '../upload.service';

/**
 * Upload files component.
 */
@Component({
  selector: 'app-files-loader',
  templateUrl: './files-loader.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PercentPipe, FileSizePipe]
})
export class FilesLoaderComponent implements OnInit {
  private readonly msalService = inject(MsalService);
  private readonly uploadService = inject(UploadService);
  private readonly fileUploadService = inject(FileUploadService);

  /**
   * Selected files.
   */
  public files = model.required<SelectedFile[]>();

  /**
   * Selected study.
   */
  public selectedStudy = input<Study>();

  /**
   * Data upload.
   */
  public entity = model.required<Upload>();

  /**
   * File metadata.
   */
  private readonly metadata = computed(() => {
    const metadata = {};

    // custom tags
    for (const tag of this.entity().tags.tags) {
      // @ts-ignore
      metadata[tag.name] = tag.value;
    }

    // study pre-defined tags
    if (this.selectedStudy()) {
      const value = escape(this.selectedStudy()!.name, this.entity().tags.valuePattern);
      if (value) {
        // @ts-ignore
        metadata[this.selectedStudy().key] = value;
      }

      for (const tag of this.selectedStudy()!.tags) {
        const v = escape(tag.value!, this.entity().tags.valuePattern);
        if (v) {
          // @ts-ignore
          metadata[tag.name] = v;
        }
      }
    }

    // user upn (email)
    const account = this.msalService.instance.getActiveAccount();
    if (account?.username) {
      // @ts-ignore
      metadata['user'] = account.username;
    }

    // @ts-ignore
    metadata['uploadID'] = this.entity().id;
    // @ts-ignore
    metadata['uploadName'] = this.entity().uploadInfo.name;
    // @ts-ignore
    metadata['collectionID'] = this.entity().uploadInfo.collection.id;
    // @ts-ignore
    metadata['collectionName'] = this.entity().uploadInfo.collection.name;

    return metadata;
  });

  public ngOnInit(): void {
    this.uploadFiles().subscribe();
  }

  /**
   * Uploads files.
   *
   * @returns Data upload.
   */
  private uploadFiles(): Observable<Upload> {
    const uploads$: Observable<boolean> = from(this.files()).pipe(
      filter((f: SelectedFile) => f.uploadResult() !== true || f.addFileResult() !== true),
      mergeMap((f: SelectedFile) => this.uploadFile(f, this.metadata()), 5)
    );

    return uploads$.pipe(
      last(),
      switchMap(_ => this.beginFinalize())
    );
  }

  /**
   * Uploads single file.
   *
   * @param file File to upload.
   * @param metadata Metadata associated with the file.
   * @returns True when file was uploaded, otherwise - false.
   */
  private uploadFile(file: SelectedFile, metadata: object): Observable<boolean> {
    if (file.uploadResult() === true)
      return of(true); // file uploaded and updated in portal

    // upload file content method
    const upload = (f: SelectedFile) => {
      f.uploadResult.set(null);
      f.progress.set(0);

      return this.fileUploadService.upload(f.uploadUrl()!, f.file, metadata, _ => { /* do nothing */ }, progress => f.progress.set(progress)).pipe(
        switchMap(_ => {
          f.uploadResult.set(true);
          return of(true);
        }),
        catchError(_ => {
          f.uploadResult.set(false);
          return of(false);
        })
      );
    };

    if (file.addFileResult() === true)
      return upload(file); // upload url requested and file updated in portal but file was not uploaded

    // add file method
    const addFile = (f: SelectedFile) => {
      f.addFileResult.set(null);

      const model: UploadFileInfo = {
        id: f.uploadUrl()!.id,
        name: f.file.name,
        size: f.file.size,
        metadata: true,
        canDelete: true,
        expiresOn: f.uploadUrl()!.expiresOn
      };

      return this.uploadService.addFile(this.entity(), model).pipe(
        switchMap(_ => {
          f.addFileResult.set(true);
          return upload(f);
        }),
        catchError(_ => {
          f.addFileResult.set(false);
          return of(false);
        })
      );
    };

    if (file.uploadUrl())
      return addFile(file); // file url requested but not updated in portal and not uploaded

    // upload url was not requested - completely new file upload
    file.addFileResult.set(null);
    return this.fileUploadService.getUploadUrl(file.file.name, this.entity().id, this.entity().uploadInfo.collection.dataProvider.country.code, false).pipe(
      switchMap(response => {
        file.uploadUrl.set(response);
        return addFile(file);
      }),
      catchError(_ => {
        file.addFileResult.set(false);
        return of(false);
      })
    );
  }

  /**
   * Removes all failed files.
   *
   * @returns Data upload.
   */
  private removeFiles(): Observable<Upload> {
    const $deletes: Observable<boolean> = from(this.files()).pipe(
      filter((f: SelectedFile) => f.uploadResult() !== true),
      mergeMap((f: SelectedFile) => this.removeFile(f), 5)
    );

    return $deletes.pipe(
      last(),
      switchMap(_ => {
        // filter removed files
        this.files.update(files => files.filter(f => f.uploadUrl()));

        return this.beginFinalize();
      })
    );
  }

  /**
   * Removes single failure file.
   *
   * @param file File to remove.
   * @returns True when remove operation succeeded, otherwise - false.
   */
  private removeFile(file: SelectedFile): Observable<boolean> {
    // file uploaded successfully or upload url was not requested
    if (file.uploadResult() === true || !file.uploadUrl())
      return of(true);

    const uploadUrl = file.uploadUrl()!;

    // delete from file upload service
    return this.fileUploadService.delete(uploadUrl).pipe(
      map(_ => {
        file.uploadUrl.set(null);
        return true;
      }),
      catchError(_ => of(false))
    ).pipe(
      // delete from portal
      mergeMap(_ => this.uploadService.deleteFile(this.entity(), uploadUrl.id).pipe(
        map(result => {
          if (result && file.addFileResult() === true)
            file.addFileResult.set(null);

          return true;
        }),
        catchError(_ => of(true))
      ))
    );
  }

  /**
   * Initiates data upload finalization.
   *
   * @returns Data upload.
   */
  private beginFinalize(): Observable<Upload> {
    if (this.files().some(f => f.uploadResult() !== true || f.addFileResult() !== true))
      return confirm('Upload completed with errors. Do you want to retry failed uploads?')
        ? this.uploadFiles() : this.removeFiles();

    return this.finalize();
  }

  /**
   * Finalizes data upload.
   *
   * @returns Data upload.
   */
  private finalize(): Observable<Upload> {
    return this.uploadService.autoSubmit(this.entity()).pipe(
      tap(entity => {
        // update entity
        this.entity.set(entity);

        // NOTE: show success notification here

        // clear data -> return back to form
        this.files.set([]);

        this.entity.set(null!);
      }),
      catchError(error => confirm('Upload finalization failed. Do you want to retry (recommended)?')
        ? this.finalize() : of(error))
    );
  }

  /**
   * Gets progress bar value.
   *
   * @param file File to process.
   * @returns Progress bar value.
   */
  protected getProgressValue(file: SelectedFile): number {
    return file.addFileResult() === true && file.uploadResult() === null ? file.progress() : 100;
  }

  /**
   * Checks if progress bar value should be shown.
   *
   * @param file File to process.
   * @returns True when progress bar value should be shown, otherwise - false.
   */
  protected showProgressValue(file: SelectedFile): boolean {
    const progress = file.progress();
    return progress > 0 && progress < 100;
  }

  /**
   * Gets progress bar type.
   *
   * @param file File to process.
   * @returns Progress bar type.
   */
  protected getProgressType(file: SelectedFile): string {
    return file.addFileResult() === false || file.uploadResult() === false ? 'danger' : 'success';
  }

  /**
   * Checks if progress bar is in indeterminate state.
   *
   * @param file File to process.
   * @returns True when progress bar is in indeterminate state, otherwise - false.
   */
  protected isIndeterminateProgress(file: SelectedFile): boolean {
    return file.uploadUrl() === null || file.addFileResult() === null;
  }
}
